import {
  BitkitCodeSnippet,
  BitkitEmptyState,
  BitkitLink,
  BitkitSegmentedControl,
  BitkitTooltip,
  IconCode,
  IconInfoCircle,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { useState } from 'react';

import { trackTerminalCommandCopied } from '@/hooks/analytics/terminalCommandTracking';
import { HookInfo } from '@/models/HookInfo';
import { TriggerParams } from '@/models/TriggerParams';
import { snakeCaseKeys } from '@/utils/changeCase';
import { startBuildPath } from '@/utils/monolithApiRouteService';

import FormBlock from '../FormBlock/FormBlock';
import { BuildConfigurationType } from '../types';
import getBuildParams from '../utils/getBuildParams';
import getCliCommand from '../utils/getCliCommand';
import getUnsupportedCliParams from '../utils/getUnsupportedCliParams';

const BITRISE_CLI_URL = 'https://docs.bitrise.io/en/bitrise-ci/bitrise-cli/bitrise-cli-overview';

export type BuildCommandsProps = {
  buildTriggerToken: string;
  data: BuildConfigurationType;
  slug: string;
};

type CommandType = 'cli' | 'curl';

const BuildCommands = (props: BuildCommandsProps) => {
  const { buildTriggerToken, data, slug } = props;

  const unsupportedCliParams = getUnsupportedCliParams(data);
  const isCliCommandAvailable = unsupportedCliParams.length === 0;

  const [commandType, setCommandType] = useState<CommandType>(isCliCommandAvailable ? 'cli' : 'curl');
  const [wasCliCommandAvailable, setWasCliCommandAvailable] = useState(isCliCommandAvailable);

  // Recommend cURL the moment a build parameter takes the CLI command away — but only then, so that
  // picking CLI back afterwards shows the empty state instead of bouncing the user to cURL again.
  if (wasCliCommandAvailable !== isCliCommandAvailable) {
    setWasCliCommandAvailable(isCliCommandAvailable);
    if (!isCliCommandAvailable) {
      setCommandType('curl');
    }
  }

  const { origin } = window.location;

  const params = {
    buildParams: getBuildParams(data) as TriggerParams,
    hookInfo: { buildTriggerToken, type: 'bitrise' } as HookInfo,
    triggeredBy: 'curl',
  };

  const curlCommand = `curl ${origin}${startBuildPath(slug)} -L --data '${JSON.stringify(snakeCaseKeys(params))}' -H "Content-Type: application/json"`;
  const isCli = commandType === 'cli';

  return (
    <FormBlock isLastItem gap="4">
      <Box display="flex" gap="4" alignItems="center">
        <Text textStyle="body/md/semibold" color="text/primary">
          Run this build from the command line
        </Text>
        <BitkitTooltip
          text="Same as clicking Start build. Copy it to trigger this build from your terminal, or reuse it in a script or another automation."
          wrapTrigger
        >
          <IconInfoCircle size="16" color="icon/tertiary" />
        </BitkitTooltip>
      </Box>
      <Box display="flex" flexDirection="column" gap="8">
        <BitkitSegmentedControl
          fullWidth
          value={commandType}
          onValueChange={({ value }) => setCommandType(value as CommandType)}
        >
          <BitkitSegmentedControl.Item value="cli">CLI</BitkitSegmentedControl.Item>
          <BitkitSegmentedControl.Item value="curl">cURL</BitkitSegmentedControl.Item>
        </BitkitSegmentedControl>
        {isCli && !isCliCommandAvailable ? (
          <BitkitEmptyState
            icon={IconCode}
            headingText="No equivalent CLI command"
            bodyText={`Bitrise CLI doesn’t support ${unsupportedCliParams.join(', ')}. Edit build parameters to use CLI, or use the cURL command instead.`}
          />
        ) : (
          <BitkitCodeSnippet
            variant="multi"
            copyLabel={isCli ? 'Copy CLI command' : 'Copy cURL command'}
            wrapLines={!isCli}
            onCopied={() =>
              trackTerminalCommandCopied({
                source: 'build_start_dialog',
                commandType,
                appSlug: slug,
                isCliCommandAvailable,
              })
            }
          >
            {isCli ? getCliCommand(slug, data) : curlCommand}
          </BitkitCodeSnippet>
        )}
      </Box>
      {!isCli && (
        <Text textStyle="comp/input/helperText" color="text/helper">
          You can run this command on any platform which has cURL installed.
        </Text>
      )}
      {isCli && isCliCommandAvailable && (
        <Text textStyle="comp/input/helperText" color="text/helper">
          You can run this command if you have Bitrise CLI installed.{' '}
          <BitkitLink colorVariant="purple" href={BITRISE_CLI_URL} isExternal>
            Learn more about the CLI
          </BitkitLink>
        </Text>
      )}
    </FormBlock>
  );
};

export default BuildCommands;
